import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { onSnapshot, collection } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { updateMissionProgress } from '../../../services/firebase';
import { Squad } from '../../../types';

interface ProgressData {
  [missionId: string]: number;
}

export const useRealTimeAchievementProgress = (
  squad: Squad | null,
  selectedLevel: { id: string; missions?: any[] } | null,
  isActiveClass: boolean
) => {
  const [achievementProgress, setAchievementProgress] = useState<ProgressData>({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Batching for persistence
  const pendingUpdatesRef = useRef<ProgressData>({});
  const batchTimeoutRef = useRef<NodeJS.Timeout | null>(null);



  // Batch persistence function
  const levelId = selectedLevel?.id || '';

  const achievementMissions = useMemo(() => {
    const missions = selectedLevel?.missions || [];
    return missions.filter((mission: any) =>
      mission && mission.type === 'measurable' && mission.unit === 'achievements' && !!mission.gameId
    );
  }, [levelId, selectedLevel?.missions]);

  const persistProgressBatch = useCallback(async () => {
    if (!squad || !levelId) return;
    if (Object.keys(pendingUpdatesRef.current).length === 0) return;

    const updates = { ...pendingUpdatesRef.current };
    pendingUpdatesRef.current = {};

    try {
      // Batch all mission updates for this level
      const persistPromises = Object.entries(updates).map(([missionId, value]) =>
        updateMissionProgress(squad.id, levelId, missionId, 'set', value)
      );

      await Promise.allSettled(persistPromises);
    } catch (error) {
      console.error('Error persisting mission progress batch:', error);
    }
  }, [squad?.id, levelId]);

  // Schedule batched persistence
  const schedulePersistence = useCallback((missionId: string, value: number) => {
    pendingUpdatesRef.current[missionId] = value;

    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
    }

    batchTimeoutRef.current = setTimeout(() => {
      persistProgressBatch();
    }, 3000); // 3 second batch window
  }, [persistProgressBatch]);

    // Track user achievement counts for calculation
  const [userAchievementCounts, setUserAchievementCounts] = useState<Record<string, Record<string, number>>>({});

  // Track previous values to avoid unnecessary persistence calls
  const previousProgressRef = useRef<ProgressData>({});

  // Calculate totals for all missions whenever user counts change
  useEffect(() => {
    if (!levelId || !isActiveClass || !squad) return;

    const newProgress: ProgressData = {};

    // Get only active student IDs (exclude teachers)
    const activeStudentIds = Object.entries(squad.attendance || {})
      .filter(([_, isActive]) => isActive)
      .map(([studentId, _]) => studentId);

    for (const mission of achievementMissions) {
      const gameId = mission.gameId as string;
      let total = 0;

      // Sum up ONLY student counts for this game (exclude teachers)
      activeStudentIds.forEach(studentId => {
        const userCounts = userAchievementCounts[studentId];
        if (userCounts) {
          total += userCounts[gameId] || 0;
        }
      });

      if (mission.id) {
        newProgress[mission.id] = total;

        // Schedule persistence if changed (using ref to avoid dependency issues)
        const oldCount = previousProgressRef.current[mission.id] || 0;
        if (total !== oldCount) {
          schedulePersistence(mission.id, total);
        }
      }
    }

    // Update ref before setting state
    previousProgressRef.current = newProgress;
    setAchievementProgress(newProgress);
  }, [userAchievementCounts, levelId, isActiveClass, schedulePersistence, squad, achievementMissions]);

  // Set up real-time listeners
  useEffect(() => {
    if (!levelId || !isActiveClass || !squad) {
      setAchievementProgress({});
      setUserAchievementCounts({});
      return;
    }

    setIsLoading(true);
    const unsubscribers: (() => void)[] = [];
    
    const teacherIds = squad.teacherIds || [];
    const activeStudentIds = Object.entries(squad.attendance || {})
      .filter(([_, isActive]) => isActive)
      .map(([studentId, _]) => studentId);

    const allUserIds = [...teacherIds, ...activeStudentIds];

    // Safeguard: Limit to reasonable number of users
    if (allUserIds.length > 20) {
      console.warn(`⚠️ Large class size (${allUserIds.length} users) - this may increase Firebase costs`);
    }

    console.log(`🔗 Setting up ${allUserIds.length} Firestore listeners for real-time progress`);

    // Set up one listener per user (not per user per game)
    for (const userId of allUserIds) {
      try {
        const achievementsRef = collection(db, 'users', userId, 'achievements');

        const unsubscribe = onSnapshot(
          achievementsRef,
          { includeMetadataChanges: false },
          (snapshot) => {
            // Count achievements per game for this user
            const gameCounts: Record<string, number> = {};
            
            snapshot.docs.forEach(doc => {
              const achievement = doc.data();
              const gameId = achievement.gameId;
              if (gameId) {
                gameCounts[gameId] = (gameCounts[gameId] || 0) + 1;
              }
            });

            // Update user counts
            setUserAchievementCounts(prev => ({
              ...prev,
              [userId]: gameCounts
            }));
          },
          (error) => {
            console.warn(`Achievement listener error for user ${userId}:`, error);
          }
        );

        unsubscribers.push(unsubscribe);
      } catch (error) {
        console.warn(`Failed to set up listener for user ${userId}:`, error);
      }
    }
    
    setIsLoading(false);

    return () => {
      console.log(`🔌 Cleaning up ${unsubscribers.length} Firestore listeners`);
      unsubscribers.forEach(unsubscribe => unsubscribe());
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        persistProgressBatch(); // Persist any pending updates
      }
    };
  }, [levelId, squad, isActiveClass, persistProgressBatch]);

  return { achievementProgress, userAchievementCounts, isLoading };
};

 